/**
 * AI on a record and on a form:
 *   <RecordAiMenu>   the "AI" button of a record page — summarise (dialog), ask the assistant about
 *                    this record, draft an e-mail about it (dialog with copy)
 *   <FieldAi>        the sparkle beside a long-text field — draft with AI, translate to the other language
 *   <ExtractLines>   the "Extract lines from text" dialog of a record's lines tab — paste, extract, add
 * Every call runs on the server under the user's rights; the person always sees and confirms.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../lib/api.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { useRoute } from '../lib/router.tsx';
import { useAi } from '../lib/ai.tsx';
import { Button, Dialog, Menu, Spinner, useToast } from './ui.tsx';
import { Icon } from './Icon.tsx';
import type { EntityMeta, FieldMeta, Row } from '../lib/types.ts';

export function RecordAiMenu({ def, id, title }: { def: EntityMeta; id: string; title: string }) {
  const { t, locale } = useI18n();
  const ai = useAi();
  const { path } = useRoute();
  const toast = useToast();
  const [dialog, setDialog] = useState<{ title: string; text: string | null } | null>(null);
  const items: Array<{ label: ReactNode; icon?: string; onClick: () => void; id?: string }> = [];
  if (ai.can('summarize')) items.push({ label: t('ai.summarise'), icon: 'FileText', id: 'ai:summarize', onClick: async () => { setDialog({ title: `${t('ai.summary')} — ${title}`, text: null }); try { const r = await ai.summarize(def.name, id); setDialog({ title: `${t('ai.summary')} — ${title}`, text: r.summary }); } catch (e) { setDialog(null); toast.error(e); } } });
  if (ai.can('assistant')) items.push({ label: t('ai.askAbout'), icon: 'MessageSquare', id: 'ai:ask', onClick: () => ai.openAssistant({ path, entity: def.name, recordId: id, title }) });
  if (ai.can('draft')) items.push({ label: t('ai.draftEmail'), icon: 'Mail', id: 'ai:email', onClick: async () => { setDialog({ title: t('ai.draftEmail'), text: null }); try { const r = await ai.draft({ entity: def.name, id, purpose: `an e-mail to the ${def.module === 'procurement' ? 'supplier' : 'customer'} about this ${def.label.toLowerCase()}`, language: locale }); setDialog({ title: t('ai.draftEmail'), text: r.text }); } catch (e) { setDialog(null); toast.error(e); } } });
  if (!items.length) return null;
  return (
    <>
      <Menu align="end" trigger={<Button icon="Sparkles" data-ai-menu>{t('ai.menu')}</Button>} items={items} />
      <Dialog open={!!dialog} onClose={() => setDialog(null)} title={dialog?.title ?? ''} width={680}
        footer={dialog?.text ? <><Button onClick={() => { void navigator.clipboard.writeText(dialog.text!); toast.info(t('common.copied')); }} icon="Copy">{t('ai.copy')}</Button><Button variant="primary" onClick={() => setDialog(null)}>{t('common.close')}</Button></> : undefined}>
        {dialog?.text === null ? <div className="row" style={{ gap: 8 }}><Spinner /> <span className="muted">{t('ai.thinking')}</span></div> : <div className="ai-text" data-ai-text>{dialog?.text}</div>}
      </Dialog>
    </>
  );
}

export function FieldAi({ field, def, record, value, onChange }: { field: FieldMeta; def: EntityMeta; record: Row | null; value: unknown; onChange: (v: string) => void }) {
  const { t, locale } = useI18n();
  const ai = useAi();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const other = locale === 'ar' ? 'en' : 'ar';
  const items: Array<{ label: ReactNode; icon?: string; onClick: () => void; id?: string }> = [];
  if (ai.can('draft')) items.push({ label: t('ai.draftField'), icon: 'Sparkles', id: 'ai:draft', onClick: async () => { setBusy(true); try { const r = await ai.draft({ entity: def.name, id: record ? String(record.id) : undefined, field: field.name, purpose: `the ${field.label.toLowerCase()} of this ${def.label.toLowerCase()}`, language: locale }); onChange(r.text); } catch (e) { toast.error(e); } finally { setBusy(false); } } });
  if (ai.can('translate')) items.push({ label: t('ai.translateTo', { lang: other === 'ar' ? 'العربية' : 'English' }), icon: 'Languages', id: 'ai:translate', onClick: async () => { const text = String(value ?? '').trim(); if (!text) return; setBusy(true); try { const r = await ai.translate(text, other); onChange(r.text); } catch (e) { toast.error(e); } finally { setBusy(false); } } });
  if (!items.length) return null;
  return <span className="field-ai"><Menu align="end" trigger={<button type="button" className="btn ghost sm" title={t('ai.menu')} data-field-ai={field.name}>{busy ? <Icon name="LoaderCircle" className="spin" size={14} /> : <Icon name="Sparkles" size={14} />}</button>} items={items} /></span>;
}

export function ExtractLines({ lineEntity, parentField, parentId, onDone }: { lineEntity: string; parentField: string; parentId: string; onDone: () => void }) {
  const { t } = useI18n();
  const ai = useAi();
  const { entities } = useMeta();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const def = entities.get(lineEntity);
  if (!ai.can('extract') || !def) return null;
  const run = async () => {
    setBusy(true);
    try { const r = await ai.extract(text, lineEntity); setRows(r.lines); setIssues(r.issues); } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const add = async () => {
    if (!rows?.length) return;
    setBusy(true);
    let n = 0;
    try {
      for (const r of rows) {
        const body: Record<string, unknown> = { [parentField]: parentId };
        for (const [k, v] of Object.entries(r)) if (def.fields.some((f) => f.name === k)) body[k] = v;
        // item / uom codes become ids when they match a record
        if (typeof r.item_code === 'string' && def.fields.some((f) => f.name === 'item_id')) { const it = await api.get<{ rows: Row[] }>('item', { 'filter[code]': r.item_code, pageSize: 1 }).catch(() => ({ rows: [] })); if (it.rows[0]) body.item_id = it.rows[0].id; }
        if (typeof r.uom_code === 'string' && def.fields.some((f) => f.name === 'uom_id')) { const u = await api.get<{ rows: Row[] }>('uom', { 'filter[code]': String(r.uom_code).toUpperCase(), pageSize: 1 }).catch(() => ({ rows: [] })); if (u.rows[0]) body.uom_id = u.rows[0].id; }
        await api.post(lineEntity, body); n++;
      }
      toast.success(t('ai.linesCreated', { n })); setOpen(false); setRows(null); setText(''); onDone();
    } catch (e) { toast.error(e); if (n) onDone(); } finally { setBusy(false); }
  };
  const cols = def.listColumns.slice(0, 6);
  return (
    <>
      <Button size="sm" icon="Sparkles" onClick={() => setOpen(true)} data-extract-lines>{t('ai.extractLines')}</Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t('ai.extractLines')} width={820}
        footer={<><Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>{rows ? <Button variant="primary" icon="Plus" busy={busy} disabled={!rows.length} onClick={() => void add()} data-extract-add>{t('ai.addLines', { n: rows.length })}</Button> : <Button variant="primary" icon="Sparkles" busy={busy} disabled={!text.trim()} onClick={() => void run()} data-extract-run>{t('ai.extractRun')}</Button>}</>}>
        <div className="muted" style={{ marginBottom: 8 }}>{t('ai.extractHint')}</div>
        <textarea className="input" rows={6} value={text} onChange={(e) => { setText(e.target.value); setRows(null); }} data-extract-text />
        {rows && (
          <div style={{ marginTop: 12 }}>
            <div className="muted" style={{ marginBottom: 6 }}>{t('ai.extractResult', { n: rows.length })}{issues.length ? ` · ${issues.join('; ')}` : ''}</div>
            <div className="table-wrap"><table className="table"><thead><tr>{cols.map((c) => <th key={c}>{def.fields.find((f) => f.name === c)?.label ?? c}</th>)}</tr></thead>
              <tbody>{rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c}>{String(r[c] ?? (c === 'item_id' ? r.item_code ?? '' : c === 'uom_id' ? r.uom_code ?? '' : ''))}</td>)}</tr>)}</tbody></table></div>
          </div>
        )}
      </Dialog>
    </>
  );
}
